import { useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import * as importApi from "../../api/import";
import { ImportEntityType, ImportReport } from "../../api/import";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Badge } from "./Badge";

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  entityType: ImportEntityType;
  title: string;
}

type Step = "scegli" | "anteprima" | "fatto";

export function ImportModal({ open, onClose, onImported, entityType, title }: ImportModalProps) {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("scegli");
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  function handleClose() {
    setStep("scegli");
    setFile(null);
    setReport(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  }

  async function handleAnalyze() {
    const selected = fileInputRef.current?.files?.[0];
    if (!selected) {
      showToast("Seleziona un file CSV o Excel.", "error");
      return;
    }
    setFile(selected);
    setIsLoading(true);
    try {
      const result = await importApi.previewImport(entityType, selected);
      setReport(result);
      setStep("anteprima");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile leggere il file."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirm() {
    if (!file) return;
    setIsLoading(true);
    try {
      const result = await importApi.commitImport(entityType, file);
      setReport(result);
      setStep("fatto");
      onImported();
    } catch (err) {
      showToast(getErrorMessage(err, "Import non riuscito."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDownloadTemplate() {
    try {
      await importApi.downloadTemplate(entityType);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile scaricare il template."), "error");
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title={title} size="lg">
      {step === "scegli" && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink/60">
            Carica un file CSV o Excel (.xlsx). Nella schermata successiva vedrai un'anteprima — nulla viene
            salvato finché non confermi.
          </p>

          <button
            type="button"
            onClick={handleDownloadTemplate}
            className="flex w-fit items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Scarica template con le intestazioni corrette
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink file:mr-3 file:rounded file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-primary file:font-semibold"
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Annulla
            </Button>
            <Button type="button" loading={isLoading} onClick={handleAnalyze}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              Analizza file
            </Button>
          </div>
        </div>
      )}

      {step === "anteprima" && report && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-[#22c55e]/10 p-3 text-center">
              <p className="text-2xl font-bold text-[#15803d]">{report.righe_valide}</p>
              <p className="text-xs text-ink/60">Valide</p>
            </div>
            <div className="rounded-lg bg-[#6b7280]/10 p-3 text-center">
              <p className="text-2xl font-bold text-[#6b7280]">{report.righe_duplicate}</p>
              <p className="text-xs text-ink/60">Già presenti</p>
            </div>
            <div className="rounded-lg bg-accent/10 p-3 text-center">
              <p className="text-2xl font-bold text-accent">{report.righe_con_errori}</p>
              <p className="text-xs text-ink/60">Con errori</p>
            </div>
          </div>

          {report.colonne_non_trovate.length > 0 && (
            <div className="rounded-lg bg-[#f97316]/10 px-3 py-2 text-xs text-[#c2410c]">
              Colonne non trovate nel file (resteranno vuote): {report.colonne_non_trovate.join(", ")}
            </div>
          )}

          <div className="max-h-64 overflow-y-auto rounded-lg border border-line">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-ink/50">Riga</th>
                  <th className="px-3 py-2 text-left font-semibold text-ink/50">Stato</th>
                  <th className="px-3 py-2 text-left font-semibold text-ink/50">Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {report.dettaglio.map((r) => (
                  <tr key={r.row_number} className="border-t border-line">
                    <td className="px-3 py-1.5">{r.row_number}</td>
                    <td className="px-3 py-1.5">
                      <Badge
                        color={r.status === "valida" ? "success" : r.status === "duplicata" ? "neutral" : "danger"}
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-ink/70">
                      {r.message ?? (r.preview ? Object.values(r.preview).filter(Boolean).join(" · ") : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setStep("scegli")}>
              Scegli un altro file
            </Button>
            <Button type="button" loading={isLoading} disabled={report.righe_valide === 0} onClick={handleConfirm}>
              Conferma import ({report.righe_valide} record)
            </Button>
          </div>
        </div>
      )}

      {step === "fatto" && report && (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <p className="text-3xl font-bold text-[#15803d]">{report.creati}</p>
          <p className="text-sm text-ink/60">record importati con successo.</p>
          {(report.righe_duplicate > 0 || report.righe_con_errori > 0) && (
            <p className="text-xs text-ink/40">
              {report.righe_duplicate} già presenti, {report.righe_con_errori} con errori — non importati.
            </p>
          )}
          <Button className="mt-2" onClick={handleClose}>
            Chiudi
          </Button>
        </div>
      )}
    </Modal>
  );
}
